import { getScrollableAncestor } from '@/utils/dom/getScrollableAncestor';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

export type UseVirtualTableRowsArgs = {
  count: number;
  /** Uniform row height (px) — every row here is the same fixed height. */
  rowHeight: number;
  overscan?: number;
  getItemKey: (index: number) => string | number;
};

export type UseVirtualTableRowsResult = {
  /** Attach to the element that sits where the table body starts. */
  wrapperRef: RefObject<HTMLDivElement>;
  virtualItems: VirtualItem[];
  /** Spacer height (px) to reserve above the rendered window. */
  before: number;
  /** Spacer height (px) to reserve below the rendered window. */
  after: number;
  /** The same scroll ancestor the virtualizer windows against (the drawer). */
  getScrollElement: () => Element | null;
};

// `useLayoutEffect` measures before paint (no misaligned-row flash) but warns
// under SSR; fall back to `useEffect` on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Windows a fixed-height row list against the nearest scrollable ancestor
 * (e.g. the drawer body) instead of an internal scroll viewport, so the table
 * keeps a single scrollbar and grows with the surrounding page — same idea as
 * the card layout's `windowScroll`.
 *
 * Rows are a uniform `rowHeight` and are deliberately never measured (no
 * `measureElement`): the consumer's rows are real, fixed-height table rows,
 * so remeasuring them on every mount would only add layout-thrash for no
 * benefit (see `VirtualTableRow`).
 */
export const useVirtualTableRows = ({
  count,
  rowHeight,
  overscan = 12,
  getItemKey,
}: UseVirtualTableRowsArgs): UseVirtualTableRowsResult => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollAncestorRef = useRef<Element | null>(null);

  const getScrollElement = useCallback((): Element | null => {
    if (!scrollAncestorRef.current) {
      scrollAncestorRef.current = getScrollableAncestor(wrapperRef.current);
    }
    return scrollAncestorRef.current ?? wrapperRef.current;
  }, []);

  // Offset of the table's top from the top of the scroll element's content.
  // Invariant to scroll position; only changes when content above the table
  // (or the layout) resizes, so it is recomputed only on those.
  const [scrollMargin, setScrollMargin] = useState(0);

  const measureScrollMargin = useCallback(() => {
    const wrapper = wrapperRef.current;
    const scrollEl = getScrollElement();
    if (!wrapper || !scrollEl || scrollEl === wrapper) return;
    const next =
      wrapper.getBoundingClientRect().top -
      scrollEl.getBoundingClientRect().top +
      scrollEl.scrollTop;
    // Guard against measurement jitter causing render churn.
    setScrollMargin((prev) => (Math.abs(prev - next) > 1 ? next : prev));
  }, [getScrollElement]);

  useIsomorphicLayoutEffect(() => {
    measureScrollMargin();

    const scrollEl = getScrollElement();
    const observer = new ResizeObserver(measureScrollMargin);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    if (scrollEl && scrollEl !== wrapperRef.current) {
      observer.observe(scrollEl);
      // The table's offset shifts whenever a sibling *above* it grows or
      // shrinks; those siblings are children of the scroll container and
      // resize without touching the wrapper or the scroll element's own
      // border box, so observing them directly keeps `scrollMargin` correct.
      Array.from(scrollEl.children).forEach((child) => observer.observe(child));
    }
    window.addEventListener('resize', measureScrollMargin);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureScrollMargin);
    };
  }, [getScrollElement, count, measureScrollMargin]);

  const virtualizer = useVirtualizer({
    count,
    getScrollElement,
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    overscan,
    getItemKey,
    scrollMargin,
  });

  // Safety net for the frame this table appears in (e.g. a layout switch): the
  // virtualizer only recomputes its visible range on a scroll/resize event, so
  // if nothing fires it can hold a stale range and render a blank window until
  // the user resizes the drawer. On the next frame, re-measure and dispatch a
  // synthetic scroll so the virtualizer re-reads the current offset and range.
  useIsomorphicLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      measureScrollMargin();
      const scrollEl = getScrollElement();
      if (scrollEl && scrollEl !== wrapperRef.current) {
        scrollEl.dispatchEvent(new Event('scroll'));
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [getScrollElement, measureScrollMargin, count]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const firstItem = virtualItems[0];
  const lastItem = virtualItems[virtualItems.length - 1];

  const before = firstItem ? firstItem.start - scrollMargin : 0;
  const after = lastItem ? totalSize - (lastItem.end - scrollMargin) : 0;

  return { wrapperRef, virtualItems, before, after, getScrollElement };
};
